// Brazilian taxpayer documents: CPF (11 digits) and CNPJ (14 characters, alphanumeric since July 2026).
const CPF_LENGTH = 11;
const CNPJ_LENGTH = 14;
const CNPJ_WEIGHTS = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const MASKS = {
  cpf: { groups: [3, 3, 3, 2], separators: ['.', '.', '-'] },
  cnpj: { groups: [2, 3, 3, 4, 2], separators: ['.', '.', '/', '-'] },
};

export const DOCUMENT_TYPES = { CPF: 'cpf', CNPJ: 'cnpj' };

export const sanitizeCpfCnpj = value =>
  `${value ?? ''}`
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .slice(0, CNPJ_LENGTH);

// A CPF only has digits, so any letter or a 12th character means CNPJ
export const getDocumentType = value => {
  const document = sanitizeCpfCnpj(value);
  const isCnpj = document.length > CPF_LENGTH || /[A-Z]/.test(document);
  return isCnpj ? DOCUMENT_TYPES.CNPJ : DOCUMENT_TYPES.CPF;
};

export const formatCpfCnpj = value => {
  const document = sanitizeCpfCnpj(value);
  const { groups, separators } = MASKS[getDocumentType(document)];

  let position = 0;
  return groups.reduce((masked, size, index) => {
    const part = document.slice(position, position + size);
    position += size;
    if (!part) return masked;
    return `${masked}${index ? separators[index - 1] : ''}${part}`;
  }, '');
};

const checkDigit = sum => {
  const remainder = sum % 11;
  return remainder < 2 ? 0 : 11 - remainder;
};

const isValidCpf = document => {
  if (!/^\d{11}$/.test(document)) return false;

  const digits = [...document].map(Number);
  return [9, 10].every(size => {
    const sum = digits
      .slice(0, size)
      .reduce((total, digit, index) => total + digit * (size + 1 - index), 0);
    return checkDigit(sum) === digits[size];
  });
};

const isValidCnpj = document => {
  if (!/^[0-9A-Z]{12}\d{2}$/.test(document)) return false;

  // Letters are valued by their ASCII code minus 48, so digits keep their face value
  const values = [...document].map(char => char.charCodeAt(0) - 48);
  return [12, 13].every(size => {
    const weights = CNPJ_WEIGHTS.slice(-size);
    const sum = values
      .slice(0, size)
      .reduce((total, value, index) => total + value * weights[index], 0);
    return checkDigit(sum) === values[size];
  });
};

export const isValidCpfCnpj = value => {
  const document = sanitizeCpfCnpj(value);
  if (!document || new Set(document).size === 1) return false;
  if (document.length === CPF_LENGTH) return isValidCpf(document);
  if (document.length === CNPJ_LENGTH) return isValidCnpj(document);
  return false;
};
